/**
 * Teleprompter: Liedtext aus dem Song holen und gesprochene Woerter im Text
 * wiederfinden. Reine Funktionen ohne Oberflaeche – dadurch testbar.
 */
import type { Song } from '../types.ts';
import { parseChordPro } from '../chordpro/parse.ts';

export interface PrompterLine {
  kind: 'text' | 'label' | 'gap';
  text: string;
  /** Nur aus dem Lead Sheet: Akkord und der Text ab dieser Stelle. */
  pairs?: { chord: string | null; text: string }[];
}

/** Woher der Teleprompter den Text nimmt. */
export type LyricsSource = 'lyrics' | 'sheet';

/** Eine Zeile wie "Refrain:" oder "[Strophe 2]" ist eine Abschnittsueberschrift. */
const LABEL_LINE = /^\s*(?:\[([^\]]{1,40})\]|([^\s:][^:]{0,38}):)\s*$/;

/** Songtext aus dem eigenen Textfeld: Leerzeile = Pause, "Refrain:" = Abschnitt. */
export function lyricsFromText(text: string): PrompterLine[] {
  const out: PrompterLine[] = [];
  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trim();
    if (!line) {
      if (out.length > 0 && out[out.length - 1]?.kind !== 'gap') out.push({ kind: 'gap', text: '' });
      continue;
    }
    const label = LABEL_LINE.exec(line);
    if (label) out.push({ kind: 'label', text: (label[1] ?? label[2] ?? '').trim() });
    else out.push({ kind: 'text', text: line.replace(/\s+/g, ' ') });
  }
  while (out[out.length - 1]?.kind === 'gap') out.pop();
  return out;
}

/** Hat der Song ein eigenes Textfeld mit Inhalt? */
export function hasOwnLyrics(song: Pick<Song, 'lyrics'>): boolean {
  return !!song.lyrics && song.lyrics.trim().length > 0;
}

/**
 * Text fuer Teleprompter und PDF. Standard: das eigene Textfeld, sonst der
 * Text aus dem Lead Sheet. Mit `source: 'sheet'` immer aus dem Lead Sheet –
 * dann mit Akkorden (pairs).
 */
export function lyricsOf(song: Pick<Song, 'body' | 'lyrics'>, source?: LyricsSource): PrompterLine[] {
  if (source !== 'sheet' && hasOwnLyrics(song)) return lyricsFromText(song.lyrics ?? '');
  return sheetLyrics(song.body ?? '');
}

/** Liedtext aus dem Lead Sheet: Textzeilen samt Akkorden, Abschnitte als Ueberschrift. */
export function sheetLyrics(body: string): PrompterLine[] {
  const out: PrompterLine[] = [];
  for (const line of parseChordPro(body).lines) {
    if (line.kind === 'row' && !line.chordsOnly) {
      const text = line.pairs.map((p) => p.text).join('').replace(/\s+/g, ' ').trim();
      if (text) out.push({ kind: 'text', text, pairs: line.pairs.map((p) => ({ chord: p.chord, text: p.text })) });
    } else if (line.kind === 'label' && line.text.trim()) {
      out.push({ kind: 'label', text: line.text.trim() });
    } else if (line.kind === 'spacer' && out.length > 0 && out[out.length - 1]?.kind !== 'gap') {
      out.push({ kind: 'gap', text: '' });
    }
  }
  // Ueberschriften ohne Text darunter (z. B. reine Akkordabschnitte) weglassen.
  return out.filter((line, i) => line.kind !== 'label' || out.slice(i + 1).find((l) => l.kind !== 'gap')?.kind === 'text');
}

/** Fuer den Vergleich: klein, ohne Satzzeichen. */
export function normalizeWord(word: string): string {
  return word
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/[^\p{L}\p{N}]/gu, '');
}

/** Zwei Woerter gelten als gleich – mit etwas Nachsicht fuer die Erkennung. */
export function sameWord(a: string, b: string): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.length >= 4 && b.length >= 4 && (a.startsWith(b) || b.startsWith(a))) return true;
  return false;
}

/**
 * Wo im Text stehen die zuletzt gehoerten Woerter?
 * Sucht ab `from` in einem Fenster nach vorn und liefert die Position NACH
 * dem letzten passenden Wort, oder -1. Nie rueckwaerts: der Text soll nicht
 * springen, nur weil ein Wort weiter oben ebenfalls vorkommt.
 */
export function followPosition(words: string[], heard: string[], from: number, window = 30): number {
  if (heard.length === 0) return -1;
  let best = -1;
  let bestLen = 0;
  const end = Math.min(words.length, from + window);
  for (let j = from; j < end; j++) {
    let len = 0;
    while (len < heard.length && j - len >= 0 && sameWord(words[j - len] ?? '', heard[heard.length - 1 - len] ?? '')) len++;
    // Ein einzelnes Wort zaehlt nur direkt am aktuellen Punkt und wenn es
    // nicht zu kurz ist – sonst springt "the" quer durch den Text.
    const ok = len >= 2 || (len === 1 && j <= from + 2 && (words[j] ?? '').length >= 3);
    if (ok && len > bestLen) {
      best = j + 1;
      bestLen = len;
    }
  }
  return best;
}

